/**
 * How one reasoning-trace step is drawn.
 *
 * ONE FIELD, not two. The wire carries `ok` AND `outcome` because they answer
 * different questions for different consumers — `ok` is "did it hard-fail",
 * which other things already depend on and which must not change meaning.
 * A trace step has exactly one question to answer: which mark to draw. Keeping
 * two fields here would force a precedence rule between them at every read
 * site, and that rule is the mapping the loop already performs once at the
 * wire boundary (`toStepStatus`). So the collapse happens there, and this type
 * gains a member instead.
 *
 * `inconclusive` is a step that RAN, did not break, and did not produce what
 * was asked — a read that refused (a filter it could not carry, a company it
 * could not resolve) or that found nothing. It used to draw the success tick.
 */
export type ReasoningStepStatus = 'running' | 'done' | 'inconclusive' | 'error';

/**
 * The mark drawn beside a finished step. `running` has none — it renders a
 * spinner instead, which is why this is only called for the other three.
 *
 * WHY AN EN DASH FOR `inconclusive`. The two existing marks are a valenced
 * pair, and the honest report for a refusal is neither of them: it is not a
 * crash (a cross would tell the user the assistant broke when it deliberately
 * declined) and it is not a success (which is the bug). A dash reads as
 * "nothing to report" at the same weight and width as the tick and cross it
 * sits in a column with, needs no colour to carry its meaning — the marks are
 * monochrome — and renders in every font, unlike the warning glyphs that would
 * otherwise be the obvious reach and which import an alarm the step does not
 * warrant.
 *
 * A pure function, exported, so the mapping is testable without rendering:
 * `refused` silently drawing a tick again is the whole defect, and a spec
 * that has to mount a transcript to catch it will not be written.
 */
export function stepStatusMark(status: ReasoningStepStatus): string {
  if (status === 'error') return '✕';
  if (status === 'inconclusive') return '–';
  return '✓';
}

type ReasoningStep = {
  id: string;
  title: string;
  description?: string;
  /** Live page-gen reasoning streamed while this step runs; rendered as a muted scrollable bubble. */
  thinking?: string;
  status: ReasoningStepStatus;
  kind: 'action';
};

export type ReasoningTrace = {
  steps: ReasoningStep[];
  startedAt: number;
  endedAt?: number;
};

export function emptyTrace(startedAt: number): ReasoningTrace {
  return { steps: [], startedAt };
}

const mapStep = (
  t: ReasoningTrace,
  id: string,
  fn: (s: ReasoningStep) => ReasoningStep
): ReasoningTrace => ({
  ...t,
  steps: t.steps.map((s) => (s.id === id ? fn(s) : s)),
});

export function startStep(t: ReasoningTrace, id: string): ReasoningTrace {
  if (t.steps.some((s) => s.id === id)) return t;
  return {
    ...t,
    steps: [
      ...t.steps,
      { id, title: 'Working', status: 'running', kind: 'action' },
    ],
  };
}

export function titleStep(
  t: ReasoningTrace,
  id: string,
  title: string
): ReasoningTrace {
  return mapStep(t, id, (s) => ({ ...s, title }));
}

export function describeStep(
  t: ReasoningTrace,
  id: string,
  description: string
): ReasoningTrace {
  return mapStep(t, id, (s) => ({ ...s, description }));
}

export function setStepThinking(
  t: ReasoningTrace,
  id: string,
  thinking: string
): ReasoningTrace {
  return mapStep(t, id, (s) => ({ ...s, thinking }));
}

export function finishStep(
  t: ReasoningTrace,
  id: string,
  status: ReasoningStepStatus
): ReasoningTrace {
  return mapStep(t, id, (s) => ({ ...s, status }));
}

export function endTrace(t: ReasoningTrace, endedAt: number): ReasoningTrace {
  return { ...t, endedAt };
}

/**
 * Reopen an ENDED trace so a follow-up phase of the same user action can add
 * its step to the same working block (e.g. the resolve-first build's
 * "Reading the page data" joining the click's build trace after landing).
 * The final `endTrace` then stamps a duration spanning the whole action.
 * Reopen-on-arrival (rather than keeping the trace open across the gap) means
 * a follow-up that never happens leaves a properly ended trace, never a
 * forever-"Working…" block.
 */
export function reopenTrace(t: ReasoningTrace): ReasoningTrace {
  const { endedAt: _endedAt, ...rest } = t;
  return rest;
}
